import { useCallback, useMemo, useState } from 'react'

import { faSearch } from '@fortawesome/pro-regular-svg-icons'
import { MultiSelect } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'

import { useTableViewConfig } from 'src/components/TableViews/TableViewConfigContext'
import { useSearchRecordTagsQuery } from 'src/fetch/records/records'
import useDebounce from 'src/hooks/use-debounce'
import { TableViewFilter } from 'src/models/TableViews/TableViewFilter'

type Props = {
  filter: TableViewFilter
  placeholder?: string
}

const RecordTagsFilter = ({ filter, placeholder }: Props) => {
  const { recordTypeId } = useTableViewConfig()
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery)

  const { tags, isFetching } = useSearchRecordTagsQuery({
    query: debouncedSearchQuery,
    recordTypeId: recordTypeId ?? undefined,
  })

  const selectedOptions = useMemo(
    () => (filter.optionsSelected ?? []).map((tag) => ({ label: tag, value: tag })),
    [filter.optionsSelected]
  )

  const searchOptions = useMemo(() => tags.map((tag) => ({ label: tag, value: tag })), [tags])

  const options = useMemo(() => {
    const optionsByValue = new Map<string, SelectOption<string>>()
    for (const option of [...selectedOptions, ...searchOptions]) {
      optionsByValue.set(option.value, option)
    }
    return Array.from(optionsByValue.values())
  }, [searchOptions, selectedOptions])

  const onSelectedOptionsChange = useCallback(
    (newSelectedOptions: SelectOption<string>[] | undefined) => {
      runInAction(() => {
        filter.optionsSelected = newSelectedOptions?.map((option) => option.value) ?? []
      })
    },
    [filter]
  )

  const getOptionKey = useCallback((option: SelectOption<string>) => option.value, [])

  return (
    <MultiSelect
      ariaLabel="Tags"
      getOptionKey={getOptionKey}
      isAsync
      isCreatable={false}
      isLoading={isFetching}
      onSearchQueryChange={setSearchQuery}
      onSelectedOptionsChange={onSelectedOptionsChange}
      options={options}
      placeholder={placeholder}
      prefixIcon={faSearch}
      selectedOptions={selectedOptions}
    />
  )
}

export default observer(RecordTagsFilter)
